import { DatePipe, HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  MatOptionModule,
} from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActivatedRoute } from '@angular/router';
import { ShareCommonComponentsModule } from '@components/common/share-common-components.module';
import { AlertNewDialogDialogComponent } from '@components/dialogs/alert-new-dialog/alert-new-dialog.component';
import { CallNotificationInfoComponent } from '@components/dialogs/call-notification-info/call-notification-info.component';
import { CallNotificationSimpleComponent } from '@components/dialogs/call-notification-simple/call-notification-simple.component';
import { CallTransferDialogComponent } from '@components/dialogs/call-transfer-dialog/call-transfer-dialog.component';
import { ConfirmDialogTypedComponent } from '@components/dialogs/confirm-dialog-typed/confirm-dialog-typed.component';
import { ConfirmDialogWithMessageComponent } from '@components/dialogs/confirm-dialog-with-message/confirm-dialog-with-message.component';
import { ConfirmDialogComponent } from '@components/dialogs/confirm-dialog/confirm-dialog.component';
import { EndChatDialogDialogComponent } from '@components/dialogs/end-chat-dialog/end-chat-dialog.component';
import { ErrorAlertDialogComponent } from '@components/dialogs/error-alert-dialog/error-alert-dialog.component';
import { SuccessAlertDialogComponent } from '@components/dialogs/success-alert-dialog/success-alert-dialog.component';
import { InfoModalModule } from '@components/info-modal/info-modal.module';
import { PreventDoubleClickDirective } from '@directives/prevent-double-click/prevent-double-click.directive';
import { environment } from '@environments/environment';
import { AdminOtherUsersService } from '@helpers/admin-other-users.service';
import { LogoffReminderService } from '@helpers/logoff-reminder.service';
import { componentStateReducer } from '@helpers/redux-devtools.helper';
import { AuthInterceptor } from '@interceptors/auth.interceptor';
import { ReleaseSmsDialogComponent } from '@modules/channels/release-sms-dialog/release-sms-dialog.component';
import { ChatListenerModule } from '@modules/chat-listener/chat-listener.module';
import { InternalChatModule } from '@modules/internal-chat/internal-chat.module';
import { SessionNotificationModalModule } from '@modules/session-notification-modal/session-notification-modal.module';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from '@node_modules/ngx-toastr';
import { ColorThemeService } from '@services/theme/color-theme.service';
import { sidebarActionsReducer } from '@store/sidebar-actions/sidebar-actions.reducer';
import { stepActionsReducer } from '@store/step-actions/step-actions.reducer';
import { CdTimerModule } from 'angular-cd-timer';
import { CookieService } from 'ngx-cookie-service';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { ChatEffects } from 'src/app/store/chat/chat.effect';
import { metaReducers } from 'src/app/store/meta-reducer/meta-reducer';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthGuard } from './guards/auth-guard.service';
import { IsAlreadyLoggedGuard } from './guards/is-already-logged-guard.service';
import { RoleGuard } from './guards/role-guard.service';
import { AppInitializerService } from './initializers/app-initializer.service';
import { TreeSelectModule } from 'primeng/treeselect';
import { PipesModule } from './pipes/pipes.module';
import { CreateCostCenterDialogComponent } from '@modules/cost-center/dialog/create-cost-center-dialog.component';
import { SideMenuService } from '@components/side-menu/side-menu.service';
import { SipService } from '@components/chat/chat-dialer/sip.service';
import { MatMenuModule } from '@node_modules/@angular/material/menu';
import { ExceptionInterceptor } from '@interceptors/exception.interceptor';
import { HighlightModule, HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import * as Sentry from '@sentry/angular-ivy';
import { IdentityProviderGuard } from './guards/identity-provider-guard.service';
import { WarningAlertDialogComponent } from '@components/dialogs/warning-alert-dialog/warning-alert-dialog.component';

const maskConfig: Partial<IConfig> = {
  validation: false,
};
registerLocaleData(localePt, 'pt');

@NgModule({
  declarations: [
    AppComponent,
    ConfirmDialogComponent,
    CreateCostCenterDialogComponent,
    ErrorAlertDialogComponent,
    SuccessAlertDialogComponent,
    WarningAlertDialogComponent,
    PreventDoubleClickDirective,
    CallTransferDialogComponent,
    CallNotificationSimpleComponent,
    CallNotificationInfoComponent,
    AlertNewDialogDialogComponent,
    ConfirmDialogWithMessageComponent,
    EndChatDialogDialogComponent,
    ReleaseSmsDialogComponent,
    ConfirmDialogTypedComponent,
  ],
  imports: [
    InfoModalModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatSelectModule,
    MatOptionModule,
    HttpClientModule,
    MatButtonModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatDialogModule,
    HighlightModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule.forRoot({
      useDefaultLang: true,
      defaultLanguage: 'en-US',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    StoreModule.forRoot(
      {
        actions: stepActionsReducer,
        sidebarActions: sidebarActionsReducer,
        componentState: componentStateReducer,
      },
      {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
        },
      }
    ),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    EffectsModule.forRoot([ChatEffects]),
    NgxMaskModule.forRoot(maskConfig),
    ToastrModule.forRoot(),
    CdTimerModule,
    FormsModule,
    PipesModule,
    SessionNotificationModalModule,
    ShareCommonComponentsModule,
    MatFormFieldModule,
    MatInputModule,
    ChatListenerModule,
    InternalChatModule,
    TreeSelectModule,
    MatMenuModule,
  ],
  providers: [
    CookieService,
    ColorThemeService,
    AdminOtherUsersService,
    {
      provide: LOCALE_ID,
      useValue: 'pt',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ExceptionInterceptor,
      multi: true,
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: (service: AppInitializerService) => {
        return async () => {
          await service.initialize();
        };
      },
      deps: [AppInitializerService, ActivatedRoute],
      multi: true,
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        fullLibraryLoader: () => import('highlight.js'),
      },
    },
    AuthGuard,
    RoleGuard,
    IsAlreadyLoggedGuard,
    DatePipe,
    LogoffReminderService,
    SideMenuService,
    SipService,
    IdentityProviderGuard,
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
